import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import type { Guest, GuestComment, GuestMonthlySummary } from "../../data/guests"
import { serializeFirestoreData } from "../../utils/timestamp"
import apiClient from "../../api/apiClient"

interface MonthlySummary {
  totalDaysThisMonth: number
  monthlyLimit: number
  showWarning: boolean
}

interface LimitCheck {
  currentDays: number
  proposedDays: number
  totalAfterRegistration: number
  monthlyLimit: number
  wouldExceedLimit: boolean
  showWarning: boolean
}

interface AdminMonthlySummary {
  building: string
  month: string
  monthlyLimit: number
  totalGuests: number
  totalDays: number
  apartments: GuestMonthlySummary[]
}

interface GuestsState {
  guests: Guest[]
  historyDisabled: boolean
  monthlySummary: MonthlySummary | null
  currentGuest: Guest | null
  adminGuests: Guest[]
  adminSummary: AdminMonthlySummary | null
  limitCheck: LimitCheck | null
  isLoading: boolean
  error: string | null
}

const initialState: GuestsState = {
  guests: [],
  historyDisabled: false,
  monthlySummary: null,
  currentGuest: null,
  adminGuests: [],
  adminSummary: null,
  limitCheck: null,
  isLoading: false,
  error: null,
}

// User thunks
export const fetchUserGuests = createAsyncThunk(
  "guests/fetchUserGuests",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/guests")
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch guests")
    }
  },
)

export const registerGuest = createAsyncThunk(
  "guests/registerGuest",
  async (
    data: { fullName: string; idNumber: string; checkInDate: string; checkOutDate: string; notes?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiClient.post("/guests", data)
      return response.data.guest
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to register guest")
    }
  },
)

export const fetchUserMonthlySummary = createAsyncThunk(
  "guests/fetchUserMonthlySummary",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/guests/monthly-summary")
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch monthly summary")
    }
  },
)

export const checkMonthlyLimit = createAsyncThunk(
  "guests/checkMonthlyLimit",
  async ({ checkInDate, checkOutDate }: { checkInDate: string; checkOutDate: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/guests/check-limit?checkInDate=${checkInDate}&checkOutDate=${checkOutDate}`)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to check limit")
    }
  },
)

// Admin thunks
export const fetchAllGuests = createAsyncThunk(
  "guests/fetchAllGuests",
  async ({ building, month }: { building?: string; month?: string } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams()
      if (building && building !== "all") params.append("building", building)
      if (month) params.append("month", month)
      const url = `/guests/admin/all${params.toString() ? `?${params.toString()}` : ""}`
      const response = await apiClient.get(url)
      return response.data.guests
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch guests")
    }
  },
)

export const fetchAdminMonthlySummary = createAsyncThunk(
  "guests/fetchAdminMonthlySummary",
  async ({ building, month }: { building: string; month?: string }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams()
      params.append("building", building)
      if (month) params.append("month", month)
      const response = await apiClient.get(`/guests/admin/monthly-summary?${params.toString()}`)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch admin summary")
    }
  },
)

export const getGuestById = createAsyncThunk(
  "guests/getGuestById",
  async (guestId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/guests/${guestId}`)
      return response.data.guest
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch guest")
    }
  },
)

export const addGuestComment = createAsyncThunk(
  "guests/addGuestComment",
  async (
    { guestId, content, attachment }: { guestId: string; content: string; attachment?: File | null },
    { rejectWithValue },
  ) => {
    try {
      let data
      let headers = {}

      if (attachment) {
        data = new FormData()
        data.append("content", content)
        data.append("attachment", attachment)
        data.append("attachmentName", attachment.name)
        data.append("attachmentType", attachment.type)
        headers = { "Content-Type": "multipart/form-data" }
      } else {
        data = { content }
        headers = { "Content-Type": "application/json" }
      }

      const response = await apiClient.post(`/guests/${guestId}/comment`, data, { headers })
      return {
        guestId,
        comment: serializeFirestoreData(response.data.comment) as GuestComment,
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to add comment")
    }
  },
)

export const replyToGuest = createAsyncThunk(
  "guests/replyToGuest",
  async ({ guestId, reply }: { guestId: string; reply: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/guests/${guestId}/reply`, { reply })
      return { guestId, adminReply: response.data.adminReply, adminReplyBy: response.data.adminReplyBy, adminReplyAt: response.data.adminReplyAt }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to save reply")
    }
  },
)

export const deleteGuest = createAsyncThunk(
  "guests/deleteGuest",
  async (guestId: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/guests/${guestId}`)
      return guestId
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to delete guest")
    }
  },
)

const guestsSlice = createSlice({
  name: "guests",
  initialState,
  reducers: {
    clearLimitCheck: (state) => {
      state.limitCheck = null
    },
    clearCurrentGuest: (state) => {
      state.currentGuest = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch user guests
      .addCase(fetchUserGuests.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchUserGuests.fulfilled, (state, action) => {
        state.guests = action.payload.guests || []
        state.historyDisabled = action.payload.historyDisabled || false
        state.isLoading = false
        state.error = null
      })
      .addCase(fetchUserGuests.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Register guest
      .addCase(registerGuest.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(registerGuest.fulfilled, (state, action: PayloadAction<Guest>) => {
        state.guests.unshift(action.payload)
        state.isLoading = false
        state.error = null
      })
      .addCase(registerGuest.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Monthly summary
      .addCase(fetchUserMonthlySummary.fulfilled, (state, action) => {
        state.monthlySummary = action.payload
      })
      // Check limit
      .addCase(checkMonthlyLimit.fulfilled, (state, action) => {
        state.limitCheck = action.payload
      })
      // Admin: fetch all guests
      .addCase(fetchAllGuests.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAllGuests.fulfilled, (state, action: PayloadAction<Guest[]>) => {
        state.adminGuests = action.payload
        state.isLoading = false
        state.error = null
      })
      .addCase(fetchAllGuests.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Admin: monthly summary
      .addCase(fetchAdminMonthlySummary.fulfilled, (state, action) => {
        state.adminSummary = action.payload
      })
      // Get guest by ID
      .addCase(getGuestById.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getGuestById.fulfilled, (state, action) => {
        state.currentGuest = action.payload
        state.isLoading = false
        state.error = null
      })
      .addCase(getGuestById.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Add guest comment
      .addCase(addGuestComment.fulfilled, (state, action) => {
        const { comment } = action.payload
        if (state.currentGuest) {
          if (!state.currentGuest.comments) {
            state.currentGuest.comments = []
          }
          state.currentGuest.comments.push(comment)
        }
      })
      // Delete guest
      .addCase(deleteGuest.fulfilled, (state, action) => {
        state.adminGuests = state.adminGuests.filter((g) => g.id !== action.payload)
        state.guests = state.guests.filter((g) => g.id !== action.payload)
      })
  },
})

export const { clearLimitCheck, clearCurrentGuest } = guestsSlice.actions
export default guestsSlice.reducer
